import { useEffect, useState, useMemo,useRef } from "react";

/**
 * CRM Column Display Management Hook
 * 
 * Domain Logic:
 * - Persists user column preferences in localStorage per storage key
 * - Handles default column configurations for different CRM views
 * - Manages column visibility with hidden field exclusions
 * - Provides column filtering interface state management
 * - Ensures column persistence across browser sessions
 * 
 * @param {Object} params - Hook parameters
 * @param {Array<string>} params.headers - All available column headers
 * @param {Array<string>} [params.defaultFields] - Default columns to show
 * @param {Array<string>} [params.hiddenFields] - Columns to exclude from selection
 * @param {string} params.storageKey - Unique key for localStorage persistence
 * @returns {Object} Column management state and functions
 */
const useDisplayedColumns = ({ headers,defaultFields,hiddenFields = [], storageKey }) => {    
  /** @type {Array<string>} Currently selected columns */
  const [selectedColumns, setSelectedColumns] = useState([]);
  /** @type {boolean} Whether column filter dropdown is open */
  const [columnsFilterDisplay, setColumnsFilterDisplay] = useState(false);
  /** @type {boolean} Whether columns have been loaded from storage */
  const loadedFromStorage = useRef(false);
const localStorageKey = `${storageKey}_digsplttayedColumns`;
  // Load from localStorage initially
  useEffect(() => {
    const saved = localStorage.getItem(localStorageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        setSelectedColumns(parsed);
      }
    }else{
      // If no saved columns, use defaultFields or all headers
      if (defaultFields && Array.isArray(defaultFields) && defaultFields.length > 0) {
        setSelectedColumns(defaultFields);
      } else {
        setSelectedColumns(headers.filter(h => !hiddenFields.includes(h)));
      }
    }
    loadedFromStorage.current = true;
  }, [storageKey]);

  // Save to localStorage
  useEffect(() => {
    console.log('loadedFromStorage.current',loadedFromStorage.current);
    
   if (!loadedFromStorage.current||!localStorageKey) {
      return;
   }
    localStorage.setItem(localStorageKey, JSON.stringify(selectedColumns));
  }, [selectedColumns, storageKey]);

  /**
   * Computed list of currently displayed columns
   * Filters headers based on selected columns
   */
  const displayedColumns = useMemo(() => {
    return headers.filter(h => selectedColumns.includes(h));
  }, [headers, selectedColumns]);

  /**
   * Toggles column visibility in the selection
   * Adds or removes columns from selected list
   */
  const changeColumnFilterDisplay = (checked, header) => {
    setSelectedColumns(prev =>
      checked
        ? [...new Set([...prev, header])]
        : prev.filter(h => h !== header)
    );
  };

  /**
   * Checks if a column is currently displayed
   */
  const isColumnDisplay = header => selectedColumns.includes(header);
  
  /**
   * Closes the column filter dropdown
   */
  const closeColumnsFilter = () => setColumnsFilterDisplay(false);

  return {
    /** @type {Array<string>} Currently displayed columns */
    displayedColumns,
    /** @type {Function} Setter for selected columns */
    setDisplayedColumns: setSelectedColumns,
    /** @type {boolean} Column filter dropdown visibility */
    columnsFilterDisplay,
    /** @type {Function} Setter for filter dropdown visibility */
    setColumnsFilterDisplay,
    /** @type {Function} Toggle column visibility */
    changeColumnFilterDisplay,
    /** @type {Function} Check if column is displayed */
    isColumnDisplay,
    /** @type {Function} Close filter dropdown */
    closeColumnsFilter,
    /** @type {Array<string>} Available columns excluding hidden ones */
    availebleColumns: headers.filter(h => !hiddenFields.includes(h)),
  };
};

export default useDisplayedColumns;
